/*
 * LabellingTests — multi-reviewer retrieval-gold labelling UI.
 *
 * Flow:
 *   1. Reviewer clicks "Login with Anchor" → SessionKit.login() opens
 *      Anchor → reviewer picks their PaSSER-allowlisted account.
 *   2. We `transact({...passlogin...}, { broadcast: false })` to obtain
 *      a Wharfkit-signed but NOT broadcast tx.
 *   3. We POST { account, packed_trx, signatures } to the labelling-api
 *      backend's /api/auth/login. The backend broadcasts the tx; the
 *      chain enforces the signature + the bcauth::passallowed whitelist
 *      + the bcauth@eosio.code permission check. On success we get a
 *      JWT, stored in localStorage('label_jwt').
 *   4. The reviewer picks an experiment from /api/experiments.
 *   5. The labelling UI mirrors `02_label_ui.py` but in React, with
 *      dynamic N-column layout (2 cfgs for academic, 3 for Jane Eyre).
 *      Per-tick autosave to /api/experiments/<name>/save → backend
 *      writes a per-reviewer CSV file (questions_<account>.csv).
 *
 * Same JWT-and-account-name flow as paper-reviewer; same package
 * deps (@wharfkit/session + @wharfkit/wallet-plugin-anchor) which are
 * already in passer's package.json.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SessionKit } from '@wharfkit/session';
import { WebRenderer } from '@wharfkit/web-renderer';
import { WalletPluginAnchor } from '@wharfkit/wallet-plugin-anchor';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import configuration from './configuration.json';

const CFG = (configuration && configuration.passer) || {};

// Pick the local labelling API when serving from localhost, otherwise
// SAME-ORIGIN — nginx is expected to proxy /api/* to the labelling-API
// Flask service. Empty base means fetch('/api/...') resolves to the
// current origin (e.g. https://passer.scpdx.net/api/...), which avoids
// CORS and mixed-content issues automatically.
//
// An operator running an old deploy (no nginx proxy) can override via
// localStorage('labellingAPI') or by setting LabellingAPIRemote in
// configuration.json to the explicit base URL (e.g.
// "http://195.230.127.227:8092").
const IS_LOCAL_HOST = typeof window !== 'undefined'
    && ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
const DEFAULT_LABELLING_API = IS_LOCAL_HOST
    ? (CFG.LabellingAPI || 'http://127.0.0.1:8092')
    : (CFG.LabellingAPIRemote || '');   // '' = same origin via nginx

const AUTH_CONTRACT  = 'bcauth';
const AUTH_ACTION    = 'passlogin';
const JWT_STORE_KEY  = 'label_jwt';
const ACCT_STORE_KEY = 'label_account';

// ---------- API client ----------
function tokenGet()   { return localStorage.getItem(JWT_STORE_KEY); }
function tokenSet(t)  { localStorage.setItem(JWT_STORE_KEY, t); }
function tokenClear() {
    localStorage.removeItem(JWT_STORE_KEY);
    localStorage.removeItem(ACCT_STORE_KEY);
}
function apiFetch(apiBase, path, init = {}) {
    const headers = { 'Content-Type': 'application/json', ...(init.headers || {}) };
    const t = tokenGet();
    if (t) headers.Authorization = `Bearer ${t}`;
    return fetch(apiBase.replace(/\/$/, '') + path, { ...init, headers });
}
async function apiJson(apiBase, path, init) {
    const r = await apiFetch(apiBase, path, init);
    if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `${r.status} ${r.statusText}`);
    }
    return r.json();
}

// ---------- Inline-styled subcomponents ----------
const PILL_PRO  = { padding: '2px 8px', borderRadius: 9999, fontSize: 11,
                    fontWeight: 600, background: '#fde68a', color: '#92400e' };
const PILL_CTRL = { padding: '2px 8px', borderRadius: 9999, fontSize: 11,
                    fontWeight: 600, background: '#e5e7eb', color: '#374151' };
const BADGE_BG  = {
    V: '#6b7280', A: '#2563eb', N: '#f59e0b', C: '#10b981',
    M: '#9333ea', /* others get default */
};

// Per-section visual treatment. Synopsis chunks get a yellowish
// background + amber border — matches the original Python stdlib
// 06_label_ui.py styling. Synopsis chunks are the "high-value" target
// for the §V.B-style summarisation questions, so giving them a
// distinct background helps the labeller spot them without reading
// every word of the section badge.
const SECTION_STYLE = {
    synopsis: { bg: '#fffbeb', border: '#fbbf24',
                badgeBg: '#fef3c7', badgeFg: '#92400e' },
    chapter:  { bg: '#ffffff', border: '#e5e7eb',
                badgeBg: '#dbeafe', badgeFg: '#1e40af' },
    section:  { bg: '#ffffff', border: '#e5e7eb',
                badgeBg: '#e0e7ff', badgeFg: '#3730a3' },
    body:     { bg: '#ffffff', border: '#e5e7eb',
                badgeBg: '#f3f4f6', badgeFg: '#374151' },
    card:     { bg: '#f5f3ff', border: '#c4b5fd',
                badgeBg: '#ede9fe', badgeFg: '#5b21b6' },
    acks:     { bg: '#ffffff', border: '#e5e7eb',
                badgeBg: '#fce7f3', badgeFg: '#9d174d' },
    _default: { bg: '#ffffff', border: '#e5e7eb',
                badgeBg: '#f3f4f6', badgeFg: '#374151' },
};

// Highlight a substring in plain text — used by the "find in chunks"
// box at the top of the labelling grid so reviewers can paste an
// expected sentence and see which chunks contain it. Case-insensitive.
function highlightMatch(text, needle) {
    if (!needle || !text) return text;
    try {
        const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`(${escaped})`, 'ig');
        const parts = text.split(re);
        return parts.map((p, i) => i % 2 === 1
            ? <mark key={i} style={{ background: '#fef08a', padding: '0 2px' }}>{p}</mark>
            : <React.Fragment key={i}>{p}</React.Fragment>);
    } catch (_) { return text; }
}

function Chunk({ id, rank, section, distance, isGold, snippet, docInfo, onToggle, findText }) {
    // docInfo is the full backend record for this chunk id:
    //   { text, coref, coref_model, coref_protagonist, embed_text }
    // coref === true  → chunk was coref-rewritten (LLM pass applied)
    // coref === false → skipped (pronoun count under threshold, or over
    //                            max input length, or ablation disabled)
    // coref === null/undef → collection has no coref metadata (vanilla
    //                        profile, or pre-coref ingest)
    // The `snippet` prop remains the plain text for the render body;
    // docInfo is for the badge and (optionally) an "Show embed text"
    // toggle when embed_text is populated.
    const [expanded, setExpanded] = React.useState(true);
    const [showEmbed, setShowEmbed] = React.useState(false);
    const clampStyle = expanded
        ? { maxHeight: 'none' }
        : {
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            maxHeight: '6em',
        };
    const sty = SECTION_STYLE[section] || SECTION_STYLE._default;
    // Find-text match boosts the visual prominence — a thin amber
    // outline on top of whatever the section colour already is.
    const hasMatch = !!(findText && snippet &&
        snippet.toLowerCase().includes(findText.toLowerCase()));
    const corefState = docInfo && Object.prototype.hasOwnProperty.call(docInfo, 'coref')
                       ? docInfo.coref : undefined;
    const embedText = (docInfo && docInfo.embed_text) || '';
    let background = isGold ? '#d1fae5' : sty.bg;
    let border     = isGold ? '#10b981' : sty.border;
    let extraShadow = '';
    if (hasMatch && !isGold) {
        background = '#fffbeb';
        border = '#f59e0b';
        extraShadow = '0 0 0 2px #fde68a';
    }
    return (
        <div style={{
            background, border: '1px solid ' + border,
            padding: '8px 10px', marginBottom: 6, borderRadius: 6,
            textAlign: 'left',
            boxShadow: extraShadow,
            transition: 'background 0.12s, border-color 0.12s',
        }}>
            <div style={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                <input type="checkbox" checked={isGold}
                       style={{ marginTop: 4, transform: 'scale(1.2)', cursor: 'pointer' }}
                       onChange={() => onToggle(id)} />
                <div style={{ flex: 1, fontFamily: 'ui-monospace, Menlo, monospace',
                              fontSize: 11, color: '#6b7280', minWidth: 0 }}>
                    <span style={{ fontWeight: 700, color: '#111827' }}>#{rank}</span>
                    <span style={{ display: 'inline-block', padding: '1px 6px',
                                   background: sty.badgeBg, color: sty.badgeFg,
                                   borderRadius: 3, marginLeft: 4,
                                   fontWeight: 600, letterSpacing: 0.2 }}>
                        {section || '?'}
                    </span>
                    <span style={{ marginLeft: 4, color: '#9ca3af', fontStyle: 'italic' }}>
                        d={distance}
                    </span>
                    {hasMatch && (
                        <span style={{ marginLeft: 6, padding: '1px 6px',
                                       background: '#fcd34d', color: '#78350f',
                                       borderRadius: 3, fontWeight: 700,
                                       fontSize: 10 }}>
                            ★ contains your text
                        </span>
                    )}
                    {/* Three-state coref badge:
                        - true  (blue)   → rewrite accepted, embedded
                        - fallback (amber) → attempted but rejected by a
                                             validator; reason on hover
                        - skipped (gray) → pre-pass rejection (short chunk
                                           / no pronouns / coref disabled)
                    */}
                    {corefState === true && (
                        <span title={`coref pass applied${docInfo?.coref_model ? ` (LLM: ${docInfo.coref_model})` : ''}${docInfo?.coref_protagonist ? `, protagonist: ${docInfo.coref_protagonist}` : ''}${docInfo?.coref_model_digest ? ` [digest: ${docInfo.coref_model_digest.slice(0, 24)}]` : ''}`}
                              style={{ marginLeft: 6, padding: '1px 6px',
                                       background: '#dbeafe', color: '#1e3a8a',
                                       borderRadius: 3, fontWeight: 700,
                                       fontSize: 10, cursor: 'help' }}>
                            coref: true
                        </span>
                    )}
                    {corefState === false && docInfo?.coref_fallback_reason && (
                        <span title={`Coref rewrite REJECTED by validator: ${docInfo.coref_fallback_reason}. Original text embedded instead. LLM: ${docInfo.coref_model || '?'}`}
                              style={{ marginLeft: 6, padding: '1px 6px',
                                       background: '#fef3c7', color: '#92400e',
                                       borderRadius: 3, fontWeight: 700,
                                       fontSize: 10, cursor: 'help',
                                       border: '1px solid #fbbf24' }}>
                            coref: fallback ({docInfo.coref_fallback_reason.split('(')[0]})
                        </span>
                    )}
                    {corefState === false && !docInfo?.coref_fallback_reason && (
                        <span title="Chunk skipped by pre-pass (below pronoun threshold, over max input length, or coref disabled by ablation flag). LLM was never called."
                              style={{ marginLeft: 6, padding: '1px 6px',
                                       background: '#f3f4f6', color: '#6b7280',
                                       borderRadius: 3, fontWeight: 700,
                                       fontSize: 10, cursor: 'help' }}>
                            coref: skipped
                        </span>
                    )}
                    {embedText && (
                        <button type="button"
                                onClick={(e) => { e.stopPropagation(); setShowEmbed(v => !v); }}
                                title="Toggle the coref-rewritten text (what was actually embedded)"
                                style={{ marginLeft: 6, padding: '1px 6px',
                                         background: showEmbed ? '#7c3aed' : '#ede9fe',
                                         color: showEmbed ? 'white' : '#5b21b6',
                                         border: 'none', borderRadius: 3,
                                         fontWeight: 700, fontSize: 10,
                                         cursor: 'pointer' }}>
                            {showEmbed ? '⏴ orig' : '⏵ embed_text'}
                        </button>
                    )}
                    <div style={{ fontSize: 10, color: '#9ca3af',
                                  wordBreak: 'break-all', marginTop: 2 }}>
                        {id}
                    </div>
                    <div onClick={() => setExpanded(e => !e)}
                         title={expanded ? 'Click to collapse' : 'Click to expand'}
                         style={{
                             marginTop: 6, fontSize: 12.5,
                             color: showEmbed ? '#5b21b6' : '#374151',
                             lineHeight: 1.5, whiteSpace: 'pre-wrap',
                             wordBreak: 'break-word', overflowWrap: 'break-word',
                             cursor: 'pointer',
                             background: showEmbed ? '#faf5ff' : 'transparent',
                             borderLeft: showEmbed ? '3px solid #7c3aed' : 'none',
                             paddingLeft: showEmbed ? 8 : 0,
                             ...clampStyle,
                         }}>
                        {(showEmbed ? embedText : snippet)
                            ? highlightMatch(showEmbed ? embedText : snippet, findText)
                            : <em>fetching…</em>}
                    </div>
                </div>
            </div>
        </div>
    );
}

function LabelGrid({ apiBase, experiment, account, onLogout }) {
    const [data, setData]           = useState(null);
    const [cur, setCur]             = useState(0);
    const [helpOpen, setHelpOpen]   = useState(true);     // show help on first visit
    const [findText, setFindText]   = useState('');       // copy-paste search box value
    const [docs, setDocs]           = useState({});       // id -> snippet
    const [saveStatus, setSaveStatus] = useState('●');     // ●/⋯/!
    // Per-config expansion: by default we show the first 50 candidates
    // (the high-recall band most likely to contain the gold chunk). The
    // reviewer can click "Show all N" per column to expand to the full
    // ranked list when the gold chunk wasn't in the top 50. Resets on
    // question change so each question starts collapsed.
    const INITIAL_VISIBLE = 50;
    const [expandedCfgs, setExpandedCfgs] = useState({});
    const saveTimer = useRef(null);
    const fetchedIds = useRef(new Set());

    const load = useCallback(async () => {
        try {
            const j = await apiJson(apiBase, `/api/experiments/${experiment}/data`);
            setData(j);
        } catch (e) {
            alert('Could not load experiment: ' + e.message);
        }
    }, [apiBase, experiment]);

    useEffect(() => { load(); }, [load]);

    const q = data && data.questions[cur];

    // Lazy-fetch chunk snippets for the current question's candidates
    useEffect(() => {
        if (!data || !q) return;
        const wanted = [];
        for (const cfg of data.configs) {
            const r = (data.retrieval[q.q_id] || {})[cfg];
            for (const id of ((r && r.ids) || [])) {
                if (!fetchedIds.current.has(id) && !docs[id]) wanted.push(id);
            }
        }
        if (!wanted.length) return;
        wanted.forEach(id => fetchedIds.current.add(id));
        apiJson(apiBase, `/api/experiments/${experiment}/docs?ids=${encodeURIComponent(wanted.join(','))}`)
            .then(j => setDocs(prev => ({ ...prev, ...(j.docs || {}) })))
            .catch(e => console.error('doc fetch failed', e));
    }, [apiBase, experiment, q, data, docs]);

    const saveAll = useCallback(() => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        setSaveStatus('⋯');
        saveTimer.current = setTimeout(async () => {
            try {
                await apiJson(apiBase, `/api/experiments/${experiment}/save`, {
                    method: 'POST',
                    body: JSON.stringify(data.questions),
                });
                setSaveStatus('●');
            } catch (e) {
                console.error(e);
                setSaveStatus('!');
            }
        }, 300);
    }, [apiBase, experiment, data]);

    const onToggle = useCallback((cfg, chunkId) => {
        if (!q) return;
        const goldField = 'gold_chunk_ids_' + cfg;
        const cur_set = new Set(((q[goldField] || '').split(',').map(s => s.trim()).filter(Boolean)));
        if (cur_set.has(chunkId)) cur_set.delete(chunkId);
        else cur_set.add(chunkId);
        const next = { ...q, [goldField]: [...cur_set].join(',') };
        const newQs = data.questions.slice();
        newQs[cur] = next;
        setData({ ...data, questions: newQs });
        saveAll();
    }, [q, data, cur, saveAll]);

    // Keyboard nav
    useEffect(() => {
        function onkey(e) {
            if (document.activeElement.tagName === 'INPUT') return;
            if (e.key === 'ArrowLeft')  setCur(c => (c - 1 + (data?.questions.length || 1)) % (data?.questions.length || 1));
            if (e.key === 'ArrowRight') setCur(c => (c + 1) % (data?.questions.length || 1));
        }
        document.addEventListener('keydown', onkey);
        return () => document.removeEventListener('keydown', onkey);
    }, [data]);

    // Each question starts collapsed (top-50 only). When the reviewer
    // navigates to a different question, reset all per-config expansions
    // so they don't accidentally label hundreds of low-ranked chunks.
    useEffect(() => { setExpandedCfgs({}); }, [cur]);

    if (!data) return <div style={{ padding: 20 }}>Loading…</div>;
    if (!q)    return <div style={{ padding: 20 }}>No questions in this experiment.</div>;

    const subset = (q.subset || '').trim();
    const subsetPill = subset === 'pronominal'
        ? <span style={PILL_PRO}>pronominal</span>
        : subset === 'control'
            ? <span style={PILL_CTRL}>control</span>
            : subset
                ? <span style={PILL_CTRL}>{subset}</span>
                : null;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
                          background: '#374151', color: 'white', fontSize: 13 }}>
                <Button size="small" label="← Prev" onClick={() => setCur(c => (c - 1 + data.questions.length) % data.questions.length)} />
                <Button size="small" label="Next →" onClick={() => setCur(c => (c + 1) % data.questions.length)} />
                <span>Q{q.q_id} of {data.questions.length}</span>
                <div style={{ display: 'flex', gap: 2, marginLeft: 12 }}>
                    {data.questions.map((qq, i) => {
                        const labelled = data.configs.some(c => qq['gold_chunk_ids_' + c]);
                        return (
                            <div key={i} title={`Q${qq.q_id}`}
                                 onClick={() => setCur(i)}
                                 style={{
                                     width: 12, height: 12, borderRadius: 2,
                                     background: labelled ? '#10b981' : '#4b5563',
                                     outline: i === cur ? '2px solid #fbbf24' : 'none',
                                     cursor: 'pointer',
                                 }} />
                        );
                    })}
                </div>
                <span style={{ marginLeft: 'auto', fontSize: 18 }} title="● saved · ⋯ saving · ! save error">
                    {saveStatus}
                </span>
                <Button size="small" label={`Logout (${account})`} severity="secondary" onClick={onLogout} />
            </div>
            <div style={{ padding: '12px 16px', background: 'white',
                          borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ marginBottom: 4 }}>
                    {subsetPill} {subsetPill && <>&nbsp;</>}
                    <strong>Q{q.q_id}:</strong> {q.q_text}
                </div>
                {q.expected_entity && <div style={{ color: '#6b7280' }}>expected entity: <code>{q.expected_entity}</code></div>}
                {q.expected_topic  && <div style={{ color: '#6b7280' }}>expected topic: <code>{q.expected_topic}</code></div>}
                {q.notes           && <div style={{ color: '#6b7280', fontStyle: 'italic' }}>notes: {q.notes}</div>}

                {/* Find-in-chunks: paste an expected sentence / phrase
                    and any chunk whose text contains it gets a thick
                    amber outline + the match is highlighted inline.
                    Helps reviewers verify they're ticking the right
                    chunk rather than reading every word. */}
                <div style={{ marginTop: 10, display: 'flex', gap: 8,
                              alignItems: 'flex-start' }}>
                    <i className="pi pi-search"
                       style={{ marginTop: 6, color: '#9ca3af' }} />
                    <textarea
                        value={findText}
                        onChange={(e) => setFindText(e.target.value)}
                        placeholder="Paste the exact text or phrase you'd expect in the gold chunk — matching chunks get highlighted"
                        rows={1}
                        style={{
                            flex: 1, fontSize: 12, fontFamily: 'inherit',
                            padding: '6px 8px', borderRadius: 6,
                            border: '1px solid #d1d5db', minHeight: 30,
                            resize: 'vertical',
                        }} />
                    {findText && (
                        <button onClick={() => setFindText('')}
                                title="Clear search"
                                style={{
                                    padding: '4px 10px', borderRadius: 6,
                                    border: '1px solid #d1d5db', background: '#f9fafb',
                                    cursor: 'pointer', fontSize: 12,
                                }}>
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Collapsible help banner — explains how to label. Visible
                on first load; reviewers can dismiss it. */}
            {helpOpen && (
                <div style={{
                    background: '#fffbeb', borderBottom: '1px solid #fde68a',
                    padding: '10px 16px', fontSize: 12.5, color: '#78350f',
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                }}>
                    <i className="pi pi-info-circle"
                       style={{ marginTop: 2, color: '#b45309' }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                            How to label
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
                            <li>Read the question above. The two (or three) columns below
                                show the top candidates from each retrieval configuration.</li>
                            <li><strong>Tick every chunk that contains a real answer to the question.</strong>
                                Multiple correct chunks are common.</li>
                            <li><strong>Yellow background</strong> = synopsis chunk
                                (the high-value summarisation target).
                                <strong> Blue badge</strong> = chapter,
                                <strong> indigo</strong> = numbered section,
                                <strong> purple</strong> = card / front-matter.</li>
                            <li>Use the <em>Paste exact text</em> field above to find
                                chunks containing a specific sentence — useful when you
                                already know the answer-bearing line.</li>
                            <li>Saves automatically after every tick. Status dot (top right):
                                <strong style={{ color: '#059669' }}> ●</strong> saved,
                                <strong style={{ color: '#d97706' }}> ⋯</strong> saving,
                                <strong style={{ color: '#dc2626' }}> !</strong> save error.</li>
                            <li>Use ← / → arrow keys to move between questions, or click
                                a green progress dot at the top.</li>
                        </ul>
                    </div>
                    <button onClick={() => setHelpOpen(false)}
                            title="Hide help"
                            style={{
                                padding: '4px 10px', borderRadius: 6,
                                border: '1px solid #fcd34d', background: 'white',
                                cursor: 'pointer', fontSize: 12, color: '#78350f',
                                fontWeight: 600,
                            }}>
                        Got it
                    </button>
                </div>
            )}
            {!helpOpen && (
                <div style={{
                    padding: '4px 16px', background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb', textAlign: 'right',
                }}>
                    <button onClick={() => setHelpOpen(true)}
                            style={{ background: 'transparent', border: 0,
                                     color: '#6b7280', fontSize: 11.5,
                                     cursor: 'pointer' }}>
                        <i className="pi pi-info-circle"
                           style={{ fontSize: 11, marginRight: 4 }} />
                        Show labelling instructions
                    </button>
                </div>
            )}
            <div style={{ display: 'grid',
                          gridTemplateColumns: data.configs.map(() => '1fr').join(' '),
                          gap: 12, padding: 12, background: '#f5f5f5' }}>
                {data.configs.map(cfg => {
                    const r = (data.retrieval[q.q_id] || {})[cfg];
                    const goldField = 'gold_chunk_ids_' + cfg;
                    const goldSet = new Set(((q[goldField] || '').split(',').map(s => s.trim()).filter(Boolean)));
                    return (
                        <div key={cfg}>
                            <h3 style={{ margin: '0 0 8px', padding: '6px 12px',
                                         background: '#1f2937', color: 'white',
                                         fontSize: 13, borderRadius: 4,
                                         display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ padding: '2px 8px', borderRadius: 4,
                                               fontSize: 11, fontWeight: 700,
                                               background: BADGE_BG[cfg] || '#6b7280' }}>
                                    {cfg}
                                </span>
                                <small style={{ opacity: 0.6, marginLeft: 'auto', fontWeight: 400 }}>
                                    {r ? r.ids.length : 0} candidates
                                </small>
                            </h3>
                            {!r
                                ? <div style={{ padding: 12, background: 'white', borderRadius: 6,
                                                color: '#6b7280', fontStyle: 'italic' }}>
                                    no retrieval data for this question/config
                                  </div>
                                : (() => {
                                    const total = r.ids.length;
                                    const expanded = !!expandedCfgs[cfg];
                                    const visible = expanded ? total : Math.min(INITIAL_VISIBLE, total);
                                    // If the reviewer already marked a chunk
                                    // outside the top-N as gold, force-expand so
                                    // they can see/un-mark it without hunting.
                                    const hasGoldBeyond = !expanded && [...goldSet].some(id => {
                                        const idx = r.ids.indexOf(id);
                                        return idx >= INITIAL_VISIBLE;
                                    });
                                    const finalVisible = (hasGoldBeyond ? total : visible);
                                    const finalExpanded = expanded || hasGoldBeyond;
                                    return (
                                        <>
                                            {r.ids.slice(0, finalVisible).map((id, i) => (
                                                <Chunk
                                                    key={id}
                                                    id={id}
                                                    rank={i + 1}
                                                    section={r.sections[i]}
                                                    distance={r.distances[i]}
                                                    isGold={goldSet.has(id)}
                                                    snippet={typeof docs[id] === 'object' && docs[id] !== null
                                                             ? docs[id].text
                                                             : docs[id]}
                                                    docInfo={typeof docs[id] === 'object' && docs[id] !== null
                                                             ? docs[id]
                                                             : undefined}
                                                    findText={findText}
                                                    onToggle={(chunkId) => onToggle(cfg, chunkId)}
                                                />
                                            ))}
                                            {total > INITIAL_VISIBLE && (
                                                <div style={{ display: 'flex', justifyContent: 'center',
                                                              padding: '10px 0' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpandedCfgs(prev => ({
                                                            ...prev, [cfg]: !finalExpanded
                                                        }))}
                                                        style={{
                                                            background: finalExpanded ? '#e5e7eb' : '#1f2937',
                                                            color: finalExpanded ? '#374151' : 'white',
                                                            border: 'none', borderRadius: 6,
                                                            padding: '8px 14px', fontSize: 12.5,
                                                            cursor: 'pointer', fontWeight: 600,
                                                        }}>
                                                        {finalExpanded
                                                            ? `▲ Collapse to top ${INITIAL_VISIBLE}`
                                                            : `▼ Show all ${total} chunks (${total - INITIAL_VISIBLE} more)`}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()
                            }
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Top-level component ----------
// App.css applies `text-align: center` to the .App wrapper; this
// component wraps everything in `textAlign: left` so the labelling
// UI doesn't inherit the centered alignment from the rest of the app.
const LabellingTests = () => {
    return (
        <div style={{ textAlign: 'left' }}>
            <LabellingTestsInner />
        </div>
    );
};

const LabellingTestsInner = () => {
    const [apiBase]       = useState(DEFAULT_LABELLING_API);
    const [account, setAccount]   = useState(localStorage.getItem(ACCT_STORE_KEY) || null);
    const [experiments, setExperiments] = useState([]);
    const [selected, setSelected] = useState(null);
    const [error, setError]       = useState(null);
    const [loading, setLoading]   = useState(false);

    // SessionKit lives in a ref so we don't reconstruct it on every render.
    const sessionKitRef = useRef(null);
    if (!sessionKitRef.current) {
        sessionKitRef.current = new SessionKit({
            appName: 'passer-labelling',
            chains: [{
                id:  configuration.passer.BCid,
                url: localStorage.getItem('bcEndpoint') || configuration.passer.BCEndPoints[0].url,
            }],
            ui: new WebRenderer(),
            walletPlugins: [new WalletPluginAnchor()],
        });
    }

    // On mount, if we have a stored JWT, validate it via /api/me. If
    // valid, fetch the experiment list. If not, prompt for re-login.
    const checkJwt = useCallback(async () => {
        if (!tokenGet()) return;
        try {
            const me = await apiJson(apiBase, '/api/me');
            setAccount(me.account);
            const j = await apiJson(apiBase, '/api/experiments');
            setExperiments(j.experiments || []);
        } catch (e) {
            tokenClear();
            setAccount(null);
        }
    }, [apiBase]);
    useEffect(() => { checkJwt(); }, [checkJwt]);

    const login = useCallback(async () => {
        setLoading(true);
        setError(null);
        let newSession = null;
        try {
            const response = await sessionKitRef.current.login();
            newSession = response.session;
            const acct = String(newSession.permissionLevel.actor);

            // Sign the passlogin action — do NOT broadcast.
            const result = await newSession.transact(
                {
                    actions: [{
                        account: AUTH_CONTRACT,
                        name:    AUTH_ACTION,
                        authorization: [{
                            actor:      newSession.permissionLevel.actor,
                            permission: newSession.permissionLevel.permission,
                        }],
                        data: { user: newSession.permissionLevel.actor },
                    }],
                },
                { broadcast: false },
            );
            // Wharfkit's `resolved.serializedTransaction` is a Uint8Array.
            const bytes = result.resolved && result.resolved.serializedTransaction;
            const packedTrx = Array.from(bytes)
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
            const signatures = result.signatures.map(s => String(s));

            const j = await apiJson(apiBase, '/api/auth/login', {
                method: 'POST',
                body: JSON.stringify({ account: acct, packed_trx: packedTrx, signatures }),
            });
            tokenSet(j.access_token);
            localStorage.setItem(ACCT_STORE_KEY, acct);
            setAccount(acct);
            const expList = await apiJson(apiBase, '/api/experiments');
            setExperiments(expList.experiments || []);
        } catch (e) {
            console.error(e);
            if (newSession) {
                try { await sessionKitRef.current.logout(newSession); } catch (_) {}
            }
            tokenClear();
            setAccount(null);
            const msg = e.message || String(e);
            setError(/authority|permission|whitelist/i.test(msg)
                ? 'Your account is not authorized for the labelling tool. Ask the project owner to passadduser your account on bcauth.'
                : `Login failed: ${msg}`);
        } finally {
            setLoading(false);
        }
    }, [apiBase]);

    const logout = useCallback(async () => {
        tokenClear();
        setAccount(null);
        setSelected(null);
        setExperiments([]);
    }, []);

    const expOptions = useMemo(
        () => experiments.map(e => ({
            label: `${e.name}  (${e.n_questions} questions, configs: ${(e.configs || []).join('/')})`,
            value: e.name,
        })),
        [experiments]
    );

    // Login + selector screens: centered, framed in a PrimeReact Card so
    // the typography and spacing match the rest of the passer app.
    // The labelling grid below uses full width — its own styling.
    const PAGE_WRAP = {
        display: 'flex', justifyContent: 'center',
        padding: '40px 16px',
        background: '#f9fafb', minHeight: 'calc(100vh - 80px)',
    };
    const CARD_INNER = { textAlign: 'left', padding: '4px 8px' };

    if (!account) {
        const header = (
            <div style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                color: 'white', padding: '20px 24px',
                borderRadius: '6px 6px 0 0',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <i className="pi pi-shield" style={{ fontSize: 28 }} />
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>
                            Retrieval Gold Labelling
                        </div>
                        <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>
                            PaSSER-RAG experimental harness
                        </div>
                    </div>
                </div>
            </div>
        );
        return (
            <div style={PAGE_WRAP}>
                <Card header={header}
                      style={{ width: 540, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      pt={{ body: { style: { padding: 0 } },
                            content: { style: { padding: 24 } } }}>
                    <div style={CARD_INNER}>
                        <p style={{ marginTop: 0, color: '#374151' }}>
                            Multi-reviewer labelling for PaSSER-RAG retrieval
                            experiments. Authentication is enforced on chain
                            via the <code>bcauth::passlogin</code> action —
                            only accounts on the <code>passallowed</code>
                            whitelist can sign in.
                        </p>
                        <Divider />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                                       margin: '12px 0' }}>
                            <Button
                                label={loading ? 'Signing transaction…' : 'Log in with Anchor'}
                                icon="pi pi-sign-in"
                                size="large"
                                disabled={loading}
                                loading={loading}
                                onClick={login}
                                style={{ flex: '0 0 auto' }} />
                            <span style={{ color: '#6b7280', fontSize: 13 }}>
                                Opens the Anchor wallet
                            </span>
                        </div>
                        {error && (
                            <div style={{ marginTop: 16 }}>
                                <Message severity="error" text={error}
                                         style={{ width: '100%' }} />
                            </div>
                        )}
                        <Divider />
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                            <div>
                                <i className="pi pi-server"
                                   style={{ fontSize: 11, marginRight: 6 }} />
                                Labelling API: <code>{apiBase}</code>
                            </div>
                            <div style={{ marginTop: 4 }}>
                                <i className="pi pi-link"
                                   style={{ fontSize: 11, marginRight: 6 }} />
                                Don't have an Anchor wallet?{' '}
                                <a href="https://www.greymass.com/anchor"
                                   target="_blank" rel="noopener noreferrer"
                                   style={{ color: '#2563eb' }}>
                                    Download here
                                </a>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (!selected) {
        const header = (
            <div style={{
                background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                color: 'white', padding: '20px 24px',
                borderRadius: '6px 6px 0 0',
            }}>
                <div style={{ display: 'flex', alignItems: 'center',
                              justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <i className="pi pi-list" style={{ fontSize: 24 }} />
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 700 }}>
                                Pick an experiment
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                                <i className="pi pi-user" style={{ marginRight: 4 }} />
                                signed in as <strong>{account}</strong>
                            </div>
                        </div>
                    </div>
                    <Button label="Logout" icon="pi pi-sign-out"
                            size="small" severity="secondary" outlined
                            onClick={logout}
                            style={{ background: 'rgba(255,255,255,0.15)',
                                     borderColor: 'rgba(255,255,255,0.3)',
                                     color: 'white' }} />
                </div>
            </div>
        );
        return (
            <div style={PAGE_WRAP}>
                <Card header={header}
                      style={{ width: 720, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      pt={{ body: { style: { padding: 0 } },
                            content: { style: { padding: 24 } } }}>
                    <div style={CARD_INNER}>
                        {experiments.length === 0 ? (
                            <Message severity="warn"
                                     text="No experiments found on the backend. An experiment requires both a questions CSV and a matching retrieval CSV."
                                     style={{ width: '100%' }} />
                        ) : (
                            <>
                                <div style={{ color: '#374151', marginBottom: 12 }}>
                                    {experiments.length} experiment{experiments.length === 1 ? '' : 's'} available on the backend.
                                </div>
                                <Dropdown
                                    value={selected}
                                    options={expOptions}
                                    onChange={(e) => setSelected(e.value)}
                                    placeholder="Select an experiment to label…"
                                    style={{ width: '100%' }} />
                                <p style={{ marginTop: 16, fontSize: 12, color: '#6b7280' }}>
                                    Your ticks are saved to a per-reviewer CSV on the
                                    server keyed by your account. The shared
                                    questions file is never modified.
                                </p>
                            </>
                        )}
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <LabelGrid
            apiBase={apiBase}
            experiment={selected}
            account={account}
            onLogout={() => { setSelected(null); }}
        />
    );
};

export default LabellingTests;
